import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {AuthHTTPService} from './auth-http/auth-http.service';
import {GenericResponse, UserAuth} from "../models/auth.models";
import {ErrorModel} from "../../../shared/models/dto.model";
import {environment} from "../../../../environments/environment";
import {UserAccount} from "../../system/models/user.model";


@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // public fields
    currentUser$: Observable<any>;
    isLoading$: Observable<boolean>;
    currentUserSubject: BehaviorSubject<any>;
    isLoadingSubject: BehaviorSubject<boolean>;
    // private fields
    private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
    private storageKey = `fluxUser`;

    constructor(
            private authHttpService: AuthHTTPService,
            private router: Router
    ) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.currentUserSubject = new BehaviorSubject<any>(this.userDetails);
        this.currentUser$ = this.currentUserSubject.asObservable();
        this.isLoading$ = this.isLoadingSubject.asObservable();
    }

    get userDetails(): Observable<any> {
        let storage: any = localStorage.getItem(this.storageKey)
        return JSON.parse(storage);
    }

    // public methods
    login(username: string, password: string, name?: string) {
        this.isLoadingSubject.next(true);
        return this.authHttpService.login(username, password).pipe(
                map((auth: any) => {
                    if (auth instanceof ErrorModel || !auth) {
                        return auth;
                    }
                    if (!auth.mfaRequired && !auth.mfaSetupRequired) {
                        this.setAuthFromLocalStorage(auth);
                    }
                    return auth;
                }),
                finalize(() => {
                  this.isLoadingSubject.next(false)
                })
        );
    }

    updateAuth(): Observable<{ status: string, value: any }> {
        this.isLoadingSubject.next(true);
        let token = this.getAuthFromLocalStorage()?.accessToken;
        if (!token) {
            return of({status: 'error', value: 'Invalid token'});
        }
        return this.authHttpService.verifyToken(token.value).pipe(
                map((auth: any) => {
                    if (auth instanceof ErrorModel || !auth) {
                        return {status: 'error', value: auth?.message || 'Invalid token'};
                    }
                    this.setAuthFromLocalStorage(auth);
                    return {status: 'success', value: auth};
                }),
                catchError((err) => {
                    return of({status: 'error', value: err.error});
                }),
                finalize(() => this.isLoadingSubject.next(false))
        );
    }

    logout() {
        localStorage.removeItem(this.storageKey);
        this.currentUserSubject.next(null);
        this.router.navigate(["/login"]);
    }

    forgotPassword(email: string): Observable<GenericResponse<string>> {
        this.isLoadingSubject.next(true);
        return this.authHttpService
                .forgotPassword(email)
                .pipe(finalize(() => this.isLoadingSubject.next(false)));
    }

    verifyMfa(code: string, mfaToken: string) {
        this.isLoadingSubject.next(true);
        return this.authHttpService.verifyMfa(code, mfaToken).pipe(
            map((auth: any) => {
                if (auth instanceof ErrorModel || !auth) {
                    return auth;
                }
                this.setAuthFromLocalStorage(auth);
                return auth;
            }),
            finalize(() => {
                this.isLoadingSubject.next(false)
            })
        );
    }

    generateMfaSetup() {
        this.isLoadingSubject.next(true);
        return this.authHttpService.generateMfaSetup().pipe(
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    enableMfa(code: string) {
        this.isLoadingSubject.next(true);
        return this.authHttpService.enableMfa(code).pipe(
            map((auth: any) => {
                if (auth instanceof ErrorModel || !auth) {
                    return auth;
                }
                this.setAuthFromLocalStorage(auth);
                return auth;
            }),
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    disableMfa() {
        this.isLoadingSubject.next(true);
        return this.authHttpService.disableMfa().pipe(
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    generateMfaSetupForForced(mfaToken: string) {
        this.isLoadingSubject.next(true);
        return this.authHttpService.generateMfaSetupForForced(mfaToken).pipe(
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    enableMfaForForced(code: string, mfaToken: string) {
        this.isLoadingSubject.next(true);
        return this.authHttpService.enableMfaForForced(code, mfaToken).pipe(
            map((auth: any) => {
                if (auth instanceof ErrorModel || !auth) {
                    return auth;
                }
                this.setAuthFromLocalStorage(auth);
                return auth;
            }),
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    getUserByToken(): Observable<any> {
        const auth = this.getAuthFromLocalStorage();
        if (!auth || !auth.accessToken) {
            return of(undefined);
        }
        this.isLoadingSubject.next(true);
        return this.authHttpService.getUserByToken(auth.accessToken.value).pipe(
                map((user: any) => {
                    if (user) {
                        this.currentUserSubject.next(user);
                    } else {
                        this.logout();
                    }
                    return user;
                }),
                finalize(() => this.isLoadingSubject.next(false))
        );
    }

    public getAuthFromLocalStorage() {
        try {
            const lsValue = localStorage.getItem(this.storageKey);
            if (!lsValue) {
                return undefined;
            }

          return JSON.parse(lsValue) as UserAuth;
        } catch (error) {
            return undefined;
        }
    }

    hasAllAuthority(authorities: string[]): boolean {
        let user = this.getAuthFromLocalStorage();
        if (!user) {
            return false;
        }
        if (!environment.enablePermission) {
            return true;
        }

        // Enhance user authorities: if user has _WRITE or _APPROVE or _FUND or _DEBIT or _CREATE, they implicitly have _READ
        let enhancedUserAuthorities = [...user.authorities];
        user.authorities.forEach(authority => {
            if (authority.endsWith('_WRITE') || authority.endsWith('_APPROVE') || authority.endsWith('_FUND') || authority.endsWith('_DEBIT') || authority.endsWith('_CREATE')) {
                const readAuthority = authority.replace(/_WRITE$|_APPROVE$|_FUND$|_DEBIT$|_CREATE$/, '_READ');
                if (!enhancedUserAuthorities.includes(readAuthority)) {
                    enhancedUserAuthorities.push(readAuthority);
                }
            }
        });

        return authorities.every((authority) => enhancedUserAuthorities.includes(authority));
    }

    hasAnyAuthority(authorities: string[]): boolean {
        let user = this.getAuthFromLocalStorage();
        if (!user) {
            return false;
        }
        if (!environment.enablePermission) {
            return true;
        }

        // Enhance user authorities: if user has _WRITE or _APPROVE or _FUND or _DEBIT or _CREATE, they implicitly have _READ
        let enhancedUserAuthorities = [...user.authorities];
        user.authorities.forEach(authority => {
            if (authority.endsWith('_WRITE') || authority.endsWith('_APPROVE') || authority.endsWith('_FUND') || authority.endsWith('_DEBIT') || authority.endsWith('_CREATE')) {
                const readAuthority = authority.replace(/_WRITE$|_APPROVE$|_FUND$|_DEBIT$|_CREATE$/, '_READ');
                if (!enhancedUserAuthorities.includes(readAuthority)) {
                    enhancedUserAuthorities.push(readAuthority);
                }
            }
        });

        return authorities.some((authority) => enhancedUserAuthorities.includes(authority));
    }

    ngOnDestroy() {
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
    }

    // private methods
    private setAuthFromLocalStorage(auth: any) {
        localStorage.setItem(this.storageKey, JSON.stringify(auth));
        return this.currentUserSubject.next(auth);
    }
}

